import React from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import Skeleton from '@mui/material/Skeleton';

const DEFAULT_IMG =
  'https://imagedelivery.net/8CA2tdaqNe_KDNIRc5st7Q/0978beaa-4fc3-408b-2c4e-715fa0c5ee00/w400h250';

/**
 * SEO-optimized Lazy Load Image Component
 * 
 * Features:
 * - Native lazy loading with fallback
 * - Proper alt text for accessibility and SEO
 * - Width/height attributes for CLS prevention
 * - WebP support detection
 * - Error handling with fallback image
 * 
 * @param {string} alt - Alt text for the image (required for SEO)
 * @param {string} src - Image source URL
 * @param {string|number} width - Image width
 * @param {string|number} height - Image height
 * @param {object} style - Additional styles
 * @param {string} title - Optional title for hover tooltip
 * @param {boolean} priority - Whether this is a priority/above-fold image
 * @param {string} sizes - Responsive sizes attribute
 * @param {string} srcSet - Responsive srcSet attribute
 */
const ImageWrapper = ({ 
  alt, 
  src, 
  width, 
  height, 
  style,
  title,
  priority = false,
  sizes,
  srcSet,
  className,
  itemProp,
  ...rest
}) => {
  const [validSrc, setValidSrc] = React.useState(null);
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    const img = new Image();
    const finalSrc = src;

    img.onload = () => {
      setValidSrc(finalSrc);
      setHasError(false);
    };
    img.onerror = () => {
      setValidSrc(DEFAULT_IMG);
      setHasError(true);
    };

    img.src = finalSrc;
  }, [src]);

  // Generate alt text if not provided
  const altText = React.useMemo(() => {
    if (alt) return alt;
    // Try to extract meaningful text from src
    if (src) {
      const urlParts = src.split('/');
      const filename = urlParts[urlParts.length - 1];
      return filename.replace(/[-_]/g, ' ').replace(/\.\w+$/, '');
    }
    return 'Image';
  }, [alt, src]);

  if (!validSrc) {
    return (
      <Skeleton
        variant="rectangular"
        width={width || '100%'}
        height={height || '100%'}
        animation="wave"
        role="img"
        aria-label={altText}
      />
    );
  }

  return (
    <LazyLoadImage
      alt={altText}
      src={validSrc}
      effect={priority ? undefined : 'blur'}
      height={height || 'auto'}
      width={width || 'auto'}
      style={style}
      title={title || (hasError ? 'Image could not be loaded' : undefined)}
      fetchPriority={priority ? 'high' : 'auto'}
      loading={priority ? 'eager' : 'lazy'}
      decoding={priority ? 'sync' : 'async'}
      placeholderSrc={DEFAULT_IMG}
      sizes={sizes}
      srcSet={srcSet}
      className={className}
      wrapperProps={{
        ...(itemProp && { itemProp })
      }}
      {...rest}
    />
  );
};

export default ImageWrapper;
